import React from "react";
import {
  CommonDeleteModal,
  CustomTooltip,
  MuiBox,
  MuiIconButton,
  MuiTypography,
} from "components/atoms";

import CloseIcon from "assets/images/svg-icons/closeIcon.svg";
import DownloadIcon from "assets/images/svg-icons/download.svg";
import MoveIcon from "assets/images/svg-icons/moveicon.svg";
import RemoveIcon from "assets/images/svg-icons/trash2.svg";
import RestoreIcon from "assets/images/svg-icons/restore.svg";
import DeleteForeverIcon from "assets/images/svg-icons/deleteforever.svg";
import StarIcon from "assets/images/svg-icons/star.svg";
import StarFillIcon from "assets/images/svg-icons/starFill.svg";
import {
  closeCenterModal,
  openCenterModal,
} from "store/slices/CenterModal.slice";
import MoveFileFolderModal from "../MoveFileFolderModal";
import LockedIcon from "assets/images/svg-icons/locked.svg";
import UnlockedIcon from "assets/images/svg-icons/unlocked.svg";
import { useDispatch, useSelector } from "react-redux";
import {
  DeletePermanentlyFilesFolderAction,
  RestoreFilesFolderAction,
  DownloadFileFolderZIPAction,
  TrashFilesFolderAction,
  StarUnstarFileFolderAction,
} from "store/middlewares/general";
import { useTranslation } from "react-i18next";
import VerifyPassword from "../VerifyPassword";

const CommonEditBar = ({
  selectedItems,
  onClose,
  page = "drive",
  isMoved = true,
  currentParentId, // folderId from FolderPage (undefined/null on My Drive)
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { drivePrefrences } = useSelector((state) => state.profile);
  const count = selectedItems?.length;

  const fileIds = selectedItems
    ?.filter((item) => item.type === "file")
    ?.map((item) => item.id);

  const folderIds = selectedItems
    ?.filter((item) => item.type === "folder")
    ?.map((item) => item.id);

  // Collect parent ids from items + optional page-level folderId fallback
  const parentIds = [
    ...new Set([
      ...(selectedItems
        ?.map(
          (item) => item.parentId ?? item.parent_id ?? item.parent?.id ?? null,
        )
        ?.filter((id) => id != null && id !== "") ?? []),
      ...(currentParentId ? [currentParentId] : []),
    ]),
  ];

  const idEditable = selectedItems.every((item) => item.permission !== "view");
  const isSharedItems = selectedItems.some(
    (item) => item.permission !== "owner",
  );

  const allStarred = selectedItems.every((item) => item.isStar);
  const allUnstarred = selectedItems.every((item) => !item.isStar);

  const isIncludeTrashed = selectedItems.some((item) => item.isTrashed);
  const isTrashed = selectedItems.every((item) => item.isTrashed);

  const itemsToUpdate =
    allStarred || allUnstarred
      ? selectedItems
      : selectedItems.filter((item) => !item.isStar);

  const payload = {
    fileIds: itemsToUpdate
      .filter((item) => item.type === "file")
      .map((item) => item.id),
    folderIds: itemsToUpdate
      .filter((item) => item.type === "folder")
      .map((item) => item.id),
    isStarred: allStarred ? false : true,
  };

  const allLocked = selectedItems.every((item) => item.isSecure);
  const allUnlocked = selectedItems.every((item) => !item.isSecure);

  const itemsToUpdateInLock =
    allLocked || allUnlocked
      ? selectedItems
      : selectedItems.filter((item) => !item.isSecure);

  const lockPayload = {
    fileIds: itemsToUpdateInLock
      .filter((item) => item.type === "file")
      .map((item) => item.id),
    folderIds: itemsToUpdateInLock
      .filter((item) => item.type === "folder")
      .map((item) => item.id),
    action: allLocked ? "unsecure" : "secure",
  };

  return (
    <MuiBox
      sx={{
        display: "flex",
        alignItems: "center",
        gap: "64px",
        p: "8px",
        height: "40px",
        borderRadius: "8px",
        backgroundColor: "background.blue200",
      }}
    >
      <MuiBox
        sx={{
          display: "flex",
          alignItems: "center",
          gap: "8px",
          width: "auto",
        }}
      >
        <MuiIconButton size={"24px"} src={CloseIcon} onClick={onClose} />
        <MuiTypography fontWeight="400" variant="14" color="text.darkBlack">
          {`${count} ${t("common.selected")}`}
        </MuiTypography>
      </MuiBox>
      <MuiBox
        sx={{
          display: "flex",
          alignItems: "center",
          gap: "16px",
          width: "auto",
        }}
      >
        {page === "trash" || isTrashed ? (
          <>
            <CustomTooltip title={t("trash.restore")}>
              <MuiIconButton
                size={"21px"}
                src={RestoreIcon}
                onClick={() =>
                  dispatch(
                    RestoreFilesFolderAction({
                      ids: [...folderIds, ...fileIds],
                      onSuccess: () => onClose(),
                    }),
                  )
                }
              />
            </CustomTooltip>
            <CustomTooltip title={t("trash.deleteForever")}>
              <MuiIconButton
                size={"21px"}
                src={DeleteForeverIcon}
                onClick={() =>
                  dispatch(
                    openCenterModal({
                      content: (
                        <CommonDeleteModal
                          title={t("trash.deleteFileForever")}
                          description={t("trash.areYouSureDeleteFileForever")}
                          btnName={t("trash.delete")}
                          onClick={() =>
                            dispatch(
                              DeletePermanentlyFilesFolderAction({
                                ids: [...folderIds, ...fileIds],
                                onSuccess: () => onClose(),
                              }),
                            )
                          }
                        />
                      ),
                    }),
                  )
                }
              />
            </CustomTooltip>
          </>
        ) : (
          <>
            {!isIncludeTrashed && (
              <CustomTooltip title={t("common.download")}>
                <MuiIconButton
                  size={"21px"}
                  src={DownloadIcon}
                  onClick={() =>
                    dispatch(
                      DownloadFileFolderZIPAction(
                        count === 1
                          ? {
                              fileId: fileIds,
                              folderId: folderIds,
                              fileName: selectedItems[0]?.name,
                            }
                          : {
                              fileId: fileIds,
                              folderId: folderIds,
                            },
                      ),
                    )
                  }
                />
              </CustomTooltip>
            )}
            {isMoved && !isIncludeTrashed && !isSharedItems && (
              <CustomTooltip title={t("common.move")}>
                <MuiIconButton
                  size={"21px"}
                  src={MoveIcon}
                  onClick={() =>
                    dispatch(
                      openCenterModal({
                        content: (
                          <MoveFileFolderModal
                            parentIds={parentIds}
                            fileIds={fileIds}
                            folderIds={folderIds}
                            onClose={onClose}
                            clearSelection={onClose}
                          />
                        ),
                      }),
                    )
                  }
                />
              </CustomTooltip>
            )}
            {!isSharedItems && !isTrashed && (
              <CustomTooltip title={t("common.moveToTrash")} arrow={true}>
                <MuiIconButton
                  size={"21px"}
                  src={RemoveIcon}
                  onClick={() =>
                    dispatch(
                      openCenterModal({
                        content: (
                          <CommonDeleteModal
                            title={t(
                              fileIds.length === 0
                                ? folderIds.length === 1
                                  ? "modal.moveToTrash.moveFolderToTrash"
                                  : "modal.moveToTrash.moveFoldersToTrash"
                                : folderIds.length === 0
                                  ? fileIds.length === 1
                                    ? "modal.moveToTrash.moveFileToTrash"
                                    : "modal.moveToTrash.moveFilesToTrash"
                                  : "common.moveToTrash",
                            )}
                            description={t(
                              fileIds.length === 0
                                ? folderIds.length === 1
                                  ? "modal.moveToTrash.moveFolderToTrashDescr"
                                  : "modal.moveToTrash.removeSelectedfolders"
                                : folderIds.length === 0
                                  ? fileIds.length === 1
                                    ? "modal.moveToTrash.moveFileToTrashDescr"
                                    : "modal.moveToTrash.removeSelectedfiles"
                                  : "modal.moveToTrash.removeSelectedItems",
                            )}
                            btnName={t("common.moveToTrash")}
                            onClick={() =>
                              dispatch(
                                TrashFilesFolderAction({
                                  ids: [...folderIds, ...fileIds],
                                  onSuccess: () => onClose(),
                                }),
                              )
                            }
                          />
                        ),
                      }),
                    )
                  }
                />
              </CustomTooltip>
            )}
            {idEditable &&
              !isIncludeTrashed &&
              drivePrefrences?.drive_secure_enabled === "true" &&
              drivePrefrences?.drive_is_secure === "true" && (
                <CustomTooltip
                  title={allLocked ? t("common.unlock") : t("common.lock")}
                  arrow={true}
                >
                  <MuiIconButton
                    size={"21px"}
                    src={allLocked ? UnlockedIcon : LockedIcon}
                    onClick={() => {
                      dispatch(
                        openCenterModal({
                          content: (
                            <VerifyPassword
                              onClose={onClose}
                              payload={lockPayload}
                            />
                          ),
                        }),
                      );
                    }}
                  />
                </CustomTooltip>
              )}
            {!isIncludeTrashed && (
              <CustomTooltip
                title={
                  allStarred
                    ? t("common.removeFormStarred")
                    : t("common.addToStarred")
                }
                arrow={true}
              >
                <MuiIconButton
                  size={"21px"}
                  src={allStarred ? StarFillIcon : StarIcon}
                  onClick={() => {
                    dispatch(StarUnstarFileFolderAction(payload));
                    dispatch(closeCenterModal());
                  }}
                />
              </CustomTooltip>
            )}
          </>
        )}
      </MuiBox>
    </MuiBox>
  );
};

export default CommonEditBar;
